import { Component, Input, TemplateRef, AfterViewInit, OnChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { IconComponent } from 'nebula-ui-kit';

export interface KeyValueItem {
  key: string;
  value?: string | number | boolean | null;
  required?: boolean;
  tooltip?: string;
  customTemplate?: TemplateRef<unknown>;
  customTemplateContext?: unknown;
}

@Component({
  selector: 'app-key-value-grid',
  standalone: true,
  imports: [CommonModule, IconComponent],
  template: `
    <div [class]="getGridClass()" [style.display]="'grid'" [style.gap]="gap">
      @for (item of items; track item.key) {
        <div class="kv-item" [class.has-custom-template]="!!item.customTemplate">
          <div class="kv-label">
            <div class="label-wrapper">
              <span class="label-text">{{ item.key }}</span>

              @if (item.required) {
                <span class="required-star">*</span>
              }

              @if (item.tooltip) {
                <div class="tooltip-wrapper" [attr.data-tooltip]="item.tooltip">
                  <lib-icon name="Info" class="info-icon" [size]="10" />
                </div>
              }
            </div>
          </div>
          <div class="kv-value">
            @if (item.customTemplate) {
              <ng-container
                *ngTemplateOutlet="
                  item.customTemplate;
                  context: item.customTemplateContext || { $implicit: item.value }
                "
              />
            } @else {
              {{ formatValue(item.value) }}
            }
          </div>
        </div>
      }
    </div>
  `,
  styles: [
    `
      .key-value-grid {
        display: grid;
        gap: 0.75rem 1.5rem;
      }

      .key-value-grid.cols-fixed {
        grid-template-columns: repeat(var(--grid-columns, 3), 1fr);
      }

      .kv-item {
        display: flex;
        flex-direction: column;
        gap: 0.375rem;
        padding: 0.375rem 0;
      }

      .kv-label {
        .label-wrapper {
          display: flex;
          align-items: center;
          gap: 0.375rem;
          flex-wrap: wrap;
        }

        .label-text {
          font-family: var(--lib-font-family-base, inherit);
          font-weight: 500;
          font-size: 0.6875rem;
          line-height: 1.2rem;
          color: var(--text-light, #6b7280);
          text-transform: uppercase;
          letter-spacing: 0.3px;
        }
      }

      /* Tablet styles (640px - 1023px) */
      @media (min-width: 640px) and (max-width: 1023px) {
        .kv-item {
          gap: 0.25rem;
          padding: 0.25rem 0;
        }

        .kv-label .label-text {
          font-size: 0.5625rem;
          line-height: 0.875rem;
          letter-spacing: 0.2px;
        }

        .kv-value {
          font-size: 0.6875rem;
          line-height: 1rem;
        }

        .required-star {
          font-size: 0.625rem;
        }

        .tooltip-wrapper .info-icon {
          width: 8px;
          height: 8px;
        }
      }

      /* Mobile styles (max-width: 639px) */
      @media (max-width: 639px) {
        .kv-item {
          padding: 0.5rem 0;
          border-bottom: 1px solid rgba(0, 0, 0, 0.05);

          &:last-child {
            border-bottom: none;
          }
        }

        .kv-label .label-text {
          font-size: 0.625rem;
        }

        .kv-value {
          font-size: 0.75rem;
        }
      }

      .required-star {
        color: #dc3545;
        font-size: 0.75rem;
        font-weight: 600;
        margin-left: 2px;
      }

      .kv-value {
        font-family: var(--lib-font-family-base, inherit);
        font-weight: 500;
        font-size: 0.8125rem;
        line-height: 1.2rem;
        color: var(--text-dark, #1f2937);
        word-break: break-word;
      }

      .tooltip-wrapper {
        position: relative;
        display: inline-flex;
        align-items: center;
        cursor: pointer;
        line-height: 1;

        .info-icon {
          width: 9px;
          height: 9px;
          color: var(--primary-color, #3b82f6);
          transition: color 0.2s ease;
          display: block;
        }

        &:hover .info-icon {
          color: var(--primary-color-dark, #2563eb);
        }

        &::before {
          content: attr(data-tooltip);
          position: absolute;
          bottom: 100%;
          left: 50%;
          transform: translateX(-50%) translateY(-8px);
          background: #1f2937;
          color: white;
          padding: 0.5rem 0.75rem;
          border-radius: 0.375rem;
          font-size: 0.75rem;
          font-weight: normal;
          line-height: 1.3;
          min-width: 180px;
          max-width: 280px;
          width: max-content;
          z-index: 10;
          opacity: 0;
          visibility: hidden;
          transition: all 0.2s ease;
          pointer-events: none;
          font-family: var(--lib-font-family-base, inherit);
          box-shadow:
            0 4px 6px -1px rgba(0, 0, 0, 0.1),
            0 2px 4px -1px rgba(0, 0, 0, 0.06);
          text-align: left;
          white-space: normal;
          word-wrap: break-word;
        }

        &::after {
          content: '';
          position: absolute;
          bottom: 100%;
          left: 50%;
          transform: translateX(-50%) translateY(0);
          border-width: 5px;
          border-style: solid;
          border-color: #1f2937 transparent transparent transparent;
          opacity: 0;
          visibility: hidden;
          transition: all 0.2s ease;
          pointer-events: none;
        }

        &:hover::before,
        &:hover::after {
          opacity: 1;
          visibility: visible;
        }

        &:hover::before {
          transform: translateX(-50%) translateY(-10px);
        }
      }

      /* Ajustes de tooltip en tablet */
      @media (min-width: 640px) and (max-width: 1023px) {
        .tooltip-wrapper::before {
          font-size: 0.6875rem;
          padding: 0.375rem 0.625rem;
          min-width: 150px;
          max-width: 240px;
        }
      }

      /* Ajustes de tooltip en móvil */
      @media (max-width: 639px) {
        .tooltip-wrapper::before {
          font-size: 0.6875rem;
          padding: 0.5rem 0.625rem;
          min-width: 140px;
          max-width: 220px;
        }
      }

      /* Posiciones específicas para tooltips */
      .kv-item:last-child .tooltip-wrapper::before {
        left: auto;
        right: 0;
        transform: translateX(0) translateY(-8px);
      }

      .kv-item:last-child .tooltip-wrapper::after {
        left: auto;
        right: 15px;
        transform: translateX(0) translateY(0);
      }

      .kv-item:last-child .tooltip-wrapper:hover::before {
        transform: translateX(0) translateY(-10px);
      }

      .kv-item:first-child .tooltip-wrapper::before {
        left: 0;
        transform: translateX(0) translateY(-8px);
      }

      .kv-item:first-child .tooltip-wrapper::after {
        left: 15px;
        transform: translateX(0) translateY(0);
      }

      .kv-item:first-child .tooltip-wrapper:hover::before {
        transform: translateX(0) translateY(-10px);
      }

      .kv-item.has-custom-template {
        .kv-value {
          display: flex;
          flex-wrap: wrap;
          align-items: center;
        }
      }

      :host-context(.dark-theme) {
        .kv-label .label-text {
          color: var(--text-light, #9ca3af);
        }

        .kv-value {
          color: var(--text-dark, #f3f4f6);
        }

        .tooltip-wrapper::before {
          background: #374151;
          color: #f3f4f6;
        }

        .tooltip-wrapper::after {
          border-color: #374151 transparent transparent transparent;
        }
      }

      /* Estilos para pantallas muy pequeñas */
      @media (max-width: 480px) {
        .tooltip-wrapper::before {
          min-width: 120px;
          max-width: 180px;
          font-size: 0.625rem;
        }
      }
    `,
  ],
})
export class KeyValueGridComponent implements AfterViewInit, OnChanges {
  @Input() items: KeyValueItem[] = [];

  @Input() columns = 3;

  @Input() gap = '0.75rem 1.5rem';

  @Input() responsive = false;

  @Input() gridClass = '';

  getGridClass(): string {
    let classes = 'key-value-grid';

    if (this.gridClass) {
      classes += ` ${this.gridClass}`;
    }

    if (!this.responsive) {
      classes += ' cols-fixed';
    }

    return classes;
  }

  ngAfterViewInit() {
    if (!this.responsive) {
      const gridElement = document.querySelector('.key-value-grid.cols-fixed');
      if (gridElement) {
        (gridElement as HTMLElement).style.setProperty('--grid-columns', this.columns.toString());
      }
    }
  }

  ngOnChanges() {
    if (!this.responsive && typeof window !== 'undefined') {
      setTimeout(() => {
        const gridElement = document.querySelector('.key-value-grid.cols-fixed');
        if (gridElement) {
          (gridElement as HTMLElement).style.setProperty('--grid-columns', this.columns.toString());
        }
      });
    }
  }

  formatValue(value: unknown): string {
    if (value === null || value === undefined) {
      return 'No definido';
    }
    if (typeof value === 'boolean') {
      return value ? 'Sí' : 'No';
    }
    return String(value);
  }
}
